import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { Box, Grid, GridProps, LinearProgress } from '@mui/material';

import { useQuery } from '@tanstack/react-query';

import { venueGetOfferStatisticsApi } from '../../api/offers/venueGetOfferStatisticsApi';
import Button from '../../components/Button/Button';
import ChipOptions from '../../components/ChipOptions/ChipOptions';
import { ShopifyProductsDialog } from '../../components/Dialogs/ShopifyProducts/ShopifyProductsDialog';
import PageTitle from '../../components/PageTitle/PageTitle';
import { enableShopifyVenueID, environment } from '../../config';
import { OfferCategory, OfferStatus, VenueListOffersFilter } from '../../graphql/API';
import { useListOfferQuery } from '../../hooks/reactQuery/useOfferQuery';
import { useFilter } from '../../hooks/useFilter';
import { useAppSelector } from '../../redux/hooks';
import { VENUE_MANUAL_TAGS } from '../../utils/venue-tags';
import AddProduct from './components/AddProduct';
import { NewProduct } from './components/NewProduct';
import ProductCard from './components/ProductCard';
import ProductCardSkeleton from './components/ProductCardSkeleton';

const commonGridProps = (key: string): GridProps => ({
  key,
  item: true,
  xs: 6,
  md: 4,
  lg: 3,
  xl: 2.4,
});

const Products: React.FC = () => {
  const navigate = useNavigate();
  const venue = useAppSelector((state) => state.venue.value);
  const isOnlineBrand = venue?.tags?.includes(VENUE_MANUAL_TAGS.Online);

  const [filters, setFilters] = useFilter<Partial<VenueListOffersFilter>>({
    category: OfferCategory.product,
  });
  const [productsDialog, setProductsDialog] = React.useState(false);

  const { data, isFetching, isLoading } = useListOfferQuery(filters);
  const { data: offerStatisticsData, isFetching: venueGetOfferStatisticsIsFetching } = useQuery({
    queryKey: ['venueGetOfferStatistics', venue?.id || ''],
    queryFn: async ({ queryKey }) => {
      const [, venueId] = queryKey as [string, string];

      return venueGetOfferStatisticsApi({
        venueID: Number(venueId),
        category: OfferCategory.product,
      });
    },
    enabled: Boolean(venue?.id),
  });

  React.useEffect(() => {
    if (!isOnlineBrand) navigate('/offers');
  }, [isOnlineBrand, navigate]);

  const totalProducts =
    (offerStatisticsData?.totalActiveOffers || 0) +
    (offerStatisticsData?.totalPendingOffers || 0) +
    (offerStatisticsData?.totalCancelledOffers || 0);

  return (
    <Box
      padding={4}
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: '#fff',
        flex: 1,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box>
        <PageTitle
          title='Your Products'
          sideComponent={
            <>
              {String(enableShopifyVenueID) === String(venue?.id) ? (
                <Button
                  variant='outlined'
                  color='primary'
                  onClick={() => {
                    setProductsDialog(true);
                  }}
                >
                  List Shopify products
                </Button>
              ) : null}
            </>
          }
        />

        <Box mt={2}>
          <ChipOptions
            name='offerStatus'
            options={[
              {
                label: `All`,
                value: '',
              },
              {
                label: `Active (${offerStatisticsData?.totalActiveOffers ?? '0'})`,
                value: OfferStatus.active,
              },
              {
                label: `In Review (${offerStatisticsData?.totalPendingOffers ?? '0'})`,
                value: OfferStatus.pending,
              },
              {
                label: `Cancelled (${offerStatisticsData?.totalCancelledOffers ?? '0'})`,
                value: OfferStatus.cancelled,
              }
            ]}
            defaultValue={filters.status ?? ''}
            onChange={({ value: v }) =>
              setFilters((prev) => ({ ...prev, status: v ? (v as OfferStatus) : undefined }))
            }
          />
        </Box>

        <Box mt={4}>
          {((!isLoading && isFetching) || venueGetOfferStatisticsIsFetching) && (
            <LinearProgress sx={{ mb: 1 }} />
          )}
        </Box>
      </Box>

      <Box sx={{ overflowY: 'auto', height: 'calc(100vh - 265px)' }}>
        {isLoading || venueGetOfferStatisticsIsFetching ? (
          <Grid container spacing={2}>
            {[...Array(4)].map((_, i) => (
              <Grid {...commonGridProps(`grid-item-${i}`)}>
                <ProductCardSkeleton />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={2}>
            <Grid {...commonGridProps('add-offer')}>
              {totalProducts === 0 ? <AddProduct /> : <NewProduct />}
            </Grid>
            {data.map((x) => (
              <Grid {...commonGridProps(x.id)}>
                <ProductCard data={x} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <ShopifyProductsDialog
        open={productsDialog}
        onClose={() => {
          setProductsDialog(false);
        }}
      />
    </Box>
  );
};

export default Products;
